import { DEMO_OWNERS, AuthTokenResponse, type DemoOwnerKey } from '@oche/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { queryClient, shouldPersistQueryKey } from '@/lib/query';
import { runVenueSwitchPrepare } from '@/lib/venue-switch';
import { DEMO_VENUES, clearAuthSession, getApiKey, getOwnerId, setAuthSession } from '@/lib/auth-store';

const BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

type AuthContextValue = {
  ready: boolean;
  switchingVenue: boolean;
  ownerId: string | null;
  apiKey: string;
  switchVenue: (key: string) => Promise<void>;
  refreshToken: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type TokenExchange = { kind: 'jwt'; token: string; ownerId: string } | { kind: 'api-key'; ownerId: string };

async function exchangeToken(apiKey: string): Promise<TokenExchange> {
  const res = await fetch(`${BASE}/auth/token`, {
    method: 'POST',
    headers: { 'x-oche-owner': apiKey },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: res.statusText }))) as { error: string };
    const demoOwner = DEMO_OWNERS[apiKey as DemoOwnerKey];
    if (res.status === 503 && body.error === 'JWT auth is not configured' && demoOwner) {
      return { kind: 'api-key', ownerId: demoOwner };
    }
    throw new Error(body.error);
  }
  const parsed = AuthTokenResponse.safeParse(await res.json());
  if (!parsed.success) throw new Error('Invalid auth response');
  return { kind: 'jwt', token: parsed.data.token, ownerId: parsed.data.ownerId };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [switchingVenue, setSwitchingVenue] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(getOwnerId());
  const [apiKey, setApiKeyState] = useState(getApiKey());

  const applySession = useCallback(async (key: string) => {
    const session = await exchangeToken(key);
    if (session.kind === 'jwt') {
      setAuthSession({ token: session.token, ownerId: session.ownerId, apiKey: key });
    } else {
      setAuthSession({ token: null, ownerId: session.ownerId, apiKey: key });
    }
    setOwnerId(session.ownerId);
    setApiKeyState(key);
    await queryClient.invalidateQueries();
  }, []);

  const refreshToken = useCallback(async () => {
    await applySession(getApiKey());
  }, [applySession]);

  const switchVenue = useCallback(
    async (key: string) => {
      if (key === getApiKey()) return;
      setSwitchingVenue(true);
      runVenueSwitchPrepare();
      clearAuthSession();
      setOwnerId(null);
      await queryClient.cancelQueries();
      queryClient.removeQueries({ predicate: (q) => shouldPersistQueryKey(q.queryKey) });
      try {
        await applySession(key);
      } finally {
        setSwitchingVenue(false);
      }
    },
    [applySession],
  );

  useEffect(() => {
    let cancelled = false;
    applySession(getApiKey())
      .catch(() => {
        /* keep api-key headers as last resort */
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const value = useMemo(
    () => ({ ready, switchingVenue, ownerId, apiKey, switchVenue, refreshToken }),
    [ready, switchingVenue, ownerId, apiKey, switchVenue, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { DEMO_VENUES };
